/**
 * The global state selectors
 */

import { createSelector } from 'reselect';
import { dig } from 'spiceutils';

const selectGlobal = (state) => state.get('global');

const selectHome = (state) => state.get('home').main;

const selectCdf = (state) => state.get('global') && state.get('global').cdf;

const selectRoute = (state) => state.get('route');

const makeSelectCurrentUser = () => createSelector(
  selectGlobal,
  (globalState) => globalState.get('currentUser')
);

const makeSelectCurrentOrg = () => createSelector(
  selectGlobal,
  (globalState) => globalState.get('currentOrg')
);

const makeSelectLoading = () => createSelector(
  selectGlobal,
  (globalState) => globalState.get('loading')
);

const makeSelectError = () => createSelector(
  selectGlobal,
  (globalState) => globalState.get('error')
);

const makeSelectRepos = () => createSelector(
  selectGlobal,
  (globalState) => globalState.getIn(['userData', 'repositories'])
);

const makeSelectCDF = () => createSelector(
  selectCdf,
  (cdfState) => cdfState.present && cdfState.present.get('cdf').toJS()
);

const makeSelectPptxCdf = () => createSelector(
  selectCdf,
  (cdfState) => dig(cdfState.present, [{ get: ['pptxCdf'] }, { toJS: [] }]) || null
);

const makeSelectControlList = () => createSelector(
  selectCdf,
  (cdfState) => cdfState.present && cdfState.present.get('controlList').toJS()
);

const makeSelectRespondToBreakGroupChange = () => createSelector(
  selectCdf,
  (cdfState) => cdfState.present && cdfState.present.get('respondToBreakGroupChange')
);

const makeSelectTemporaryControlList = () => createSelector(
  selectHome,
  (homeState) => dig(homeState, [{ get: ['tempControlList'] }, { toJS: [] }])
);

const makeSelectClientPackage = () => createSelector(
  selectHome,
  (homeState) => homeState.get('clientPackage')
);

const makeSelectQuestionSuggestions = () => createSelector(
  selectHome,
  (homeState) => homeState.get('questionSuggestions').toJS()
);

const makeSelectQuestionControlList = () => createSelector(
  selectHome,
  (homeState) => homeState && homeState.get('questionControlList').toJS()
);

const makeSelectMetaJSON = () => createSelector(
  selectHome,
  (homeState) => homeState && homeState.get('metaJSON').toJS()
);

const makeSelectLocation = () => createSelector(
  selectRoute,
  (routeState) => routeState.get('location').toJS()
);

const makeSelectPptxDownloadActive = () => createSelector(
  selectHome,
  (homeState) => homeState.get('pptxDownloadActive')
);

const makeSelectPptxDownloadUrl = () => createSelector(
  selectHome,
  (homeState) => homeState !== undefined ? homeState.get('pptxDownloadUrl') : null
);

const makeSelectPptxDownloadFailed = () => createSelector(
  selectHome,
  (homeState) => homeState.get('pptxDownloadFailed')
);

const makeSelectPptxDownloadFailedStatus = () => createSelector(
  selectHome,
  (homeState) => homeState.get('pptxDownloadFailedStatus')
);

const makeSelectCsvDownloadUrl = () => createSelector(
  selectHome,
  (homeState) => homeState !== undefined ? homeState.get('csvDownloadUrl') : null
);

const makeSelectPresetList = (listType) => createSelector(
  selectHome,
  (homeState) => homeState.get(`preset${listType}s`).toJS()
);

const makeSelectShowNewQuestionSelector = () => createSelector(
  selectHome,
  (homeState) => homeState.get('showNewQuestionSelector')
);

const makeSelectHasFutureCDFs = () => createSelector(
  selectCdf,
  (cdfState) => dig(cdfState, ['future', 'length']) > 0
);

const makeSelectHasPastCDFs = () => createSelector(
  selectCdf,
  (cdfState) => dig(cdfState, ['past', 'length']) > 0
);

const makeSelectChartLoading = () => createSelector(
  selectHome,
  (homeState) => homeState.get('chartLoading')
);

const makeSelectPageLoading = () => createSelector(
  selectHome,
  (homeState) => homeState.get('pageLoading')
);

const makeSelectChartError = () => createSelector(
  selectHome,
  (homeState) => homeState.get('chartError')
);

const makeSelectRole = () => createSelector(
  selectHome,
  (homeState) => homeState.get('role')
);

const makeSelectPresetListName = (field) => createSelector(
  selectCdf,
  (cdfState) => cdfState.present && cdfState.present.get(`preset${field}`)
);

const makeSelectMetaData = (_questionCode) => createSelector(
  selectCdf,
  (cdfState) => {
    const state = cdfState.present;
    if (state) {
      const metaData = state.get('metaData').toJS();
      if (metaData) {
        const questionCode = _questionCode || state.getIn(['cdf', 'QuestionCode']);
        if (questionCode) {
          // Return question-specific meta data if we have a question code
          return metaData[questionCode];
        }
        // Else return whatever meta data we have
        return Object.values(metaData)[0];
      }
    }
    return {};
  }
);

export {
  selectGlobal,
  selectHome,
  makeSelectCurrentUser,
  makeSelectCurrentOrg,
  makeSelectLoading,
  makeSelectError,
  makeSelectRepos,
  makeSelectLocation,

  makeSelectCDF,
  makeSelectPptxCdf,
  makeSelectControlList,
  makeSelectRespondToBreakGroupChange,
  makeSelectTemporaryControlList,
  makeSelectClientPackage,
  makeSelectMetaJSON,
  makeSelectMetaData,

  makeSelectQuestionSuggestions,
  makeSelectQuestionControlList,

  makeSelectPptxDownloadActive,
  makeSelectPptxDownloadUrl,
  makeSelectPptxDownloadFailed,
  makeSelectPptxDownloadFailedStatus,
  makeSelectCsvDownloadUrl,

  makeSelectPresetList,
  makeSelectPresetListName,

  makeSelectShowNewQuestionSelector,
  makeSelectHasFutureCDFs,
  makeSelectHasPastCDFs,
  makeSelectChartLoading,
  makeSelectChartError,
  makeSelectPageLoading,

  makeSelectRole,
};



// WEBPACK FOOTER //
// ./app/containers/App/selectors.js